import { endOfDay, startOfDay, subDays } from 'date-fns';

import type { DateRange, DateRangePreset } from './types';

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: 'Today',
    value: 'today',
    getRange: () => {
      const today = new Date();
      return {
        start: startOfDay(today),
        end: endOfDay(today),
      };
    },
  },
  {
    label: 'Yesterday',
    value: 'yesterday',
    getRange: () => {
      const yesterday = subDays(new Date(), 1);
      return {
        start: startOfDay(yesterday),
        end: endOfDay(yesterday),
      };
    },
  },
  {
    label: 'Last 7 days',
    value: 'last7days',
    getRange: () => {
      const today = new Date();
      return {
        start: startOfDay(subDays(today, 6)),
        end: endOfDay(today),
      };
    },
  },
  {
    label: 'Last 14 days',
    value: 'last14days',
    getRange: () => {
      const today = new Date();
      return {
        start: startOfDay(subDays(today, 13)),
        end: endOfDay(today),
      };
    },
  },
  {
    label: 'Last 30 days',
    value: 'last30days',
    getRange: () => {
      const today = new Date();
      return {
        start: startOfDay(subDays(today, 29)),
        end: endOfDay(today),
      };
    },
  },
  {
    label: 'Last 90 days',
    value: 'last90days',
    getRange: () => {
      const today = new Date();
      return {
        start: startOfDay(subDays(today, 89)),
        end: endOfDay(today),
      };
    },
  },
];

export const CUSTOM_RANGE_OPTION: DateRangePreset = {
  label: 'Custom range',
  value: 'custom',
  getRange: () => ({
    start: null,
    end: null,
  }),
};

export function findMatchingPreset(range: DateRange): DateRangePreset | null {
  if (!range.start || !range.end) {
    return null;
  }

  // Get the date components in local time
  const startYear = range.start.getFullYear();
  const startMonth = range.start.getMonth();
  const startDay = range.start.getDate();

  const endYear = range.end.getFullYear();
  const endMonth = range.end.getMonth();
  const endDay = range.end.getDate();

  for (const preset of DATE_RANGE_PRESETS) {
    const presetRange = preset.getRange();
    if (presetRange.start && presetRange.end) {
      // Compare using local date components, not timestamps
      const presetStartYear = presetRange.start.getFullYear();
      const presetStartMonth = presetRange.start.getMonth();
      const presetStartDay = presetRange.start.getDate();

      const presetEndYear = presetRange.end.getFullYear();
      const presetEndMonth = presetRange.end.getMonth();
      const presetEndDay = presetRange.end.getDate();

      const matches =
        startYear === presetStartYear &&
        startMonth === presetStartMonth &&
        startDay === presetStartDay &&
        endYear === presetEndYear &&
        endMonth === presetEndMonth &&
        endDay === presetEndDay;

      if (matches) {
        return preset;
      }
    }
  }

  return null;
}

export function formatDateRange(range: DateRange): string {
  if (!range.start && !range.end) {
    return 'Select date range';
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (range.start && range.end) {
    const startStr = formatDate(range.start);
    const endStr = formatDate(range.end);

    // If same day, show only once
    if (startStr === endStr) {
      return startStr;
    }

    return `${startStr} - ${endStr}`;
  }

  if (range.start) {
    return `From ${formatDate(range.start)}`;
  }

  if (range.end) {
    return `Until ${formatDate(range.end)}`;
  }

  return 'Select date range';
}
